/* ═══════════════════════════════════════════════════════════════════════════════
   PREPAID IMPORT RECONCILE — the Step-6 import must not silently destroy a
   schedule the operator entered in the app.
   ───────────────────────────────────────────────────────────────────────────────

   THE DEFECT THIS EXISTS FOR (measured, diffs/phase3-clobber-phase0.txt):
   `/api/:companyId/onboarding/import-vendors` DELETEs every prepaid_contracts row
   for the company (server.js:3116) and re-INSERTs from the payload — and it does so
   for a DRAFT save too, because the draft gate sits AFTER the DELETE (server.js:3271).
   When the payload is built from a FRESH WORKBOOK PARSE, anything the operator
   entered in the app that the workbook does not carry is gone: measured at
   15 rows / $76,957.53 of forecast amortization on cid=3 and 9 rows / $71,957.52 on
   cid=8 / 36 / 40, of which the six Conferences event bookings are $61,645.00.

   ★★ AND THE FINDING THAT SHAPES THE FIX: the client ALREADY has the read-back.
   `loadStep6ForEdit` (AdminPage) re-loads the stored contracts through
   `getPrepaidContracts` and its re-map CARRIES the schedule — so rebuilding the
   payload from THAT producer loses NOTHING (measured 0 rows / 0 cells / 0 orphans on
   all four tenants). The defect is not "there is no guard"; it is that the
   fresh-upload producer never consults the read-back the edit producer already uses.
   ⇒ This module is what makes the two producers agree. It is the §12.5
     `billing_anchor` shape: ONE payload builder, TWO callers handing it different
     inputs — Principle 0 is necessary and not sufficient.

   ⛔ WHAT THIS DELIBERATELY IS NOT:
     · NOT a Σ(amort)-vs-prepaid_amount check. MEASURED miscalibrated: it fires on
       22-26 rows on the SAFE re-entry path where nothing is lost, and is SILENT on
       all six Conferences rows (each stores Σ == prepaid exactly). That is §12.5
       shape 10 — a check that fires on correct data — and "a check that always fires
       is a check nobody reads".
     · NOT a provenance / `source` column. Stamping today's rows would be a SEED, not
       a backfill (§5's test: the system run today would NOT produce those values),
       and there is no marker to seed from — `event_month` is set on 6 rows on cid=3,
       1 on cid=36 and ZERO on cid=8 / 40 while all four hold the same six schedules.
     · NOT a merge. The import still DELETEs and re-INSERTs the whole set
       (§12.5 IMPORT-MUST-REPLACE); the operator decides what the payload SAYS.

   ★ FORECAST-ONLY, BY MEASUREMENT. Only months > cutoff are ever considered. The
   workbook reproduces closed history exactly — 686 (cid=3) / 680 (cid=8/36/40) closed
   cells, ZERO lost on every tenant — so extending this to `m <= cutoff` would add no
   coverage and would start flagging the cid=3 rounding-remainder residue ($0.10 /
   $0.05 / $0.05 / $0.04), i.e. it would become the Σ-check by another route.
   ⓘ `is_historical` (the COLUMN) is NOT the discriminator and must not be used: the
     import writes `p.is_historical !== false` on essentially every row, and it
     describes the ROW's provenance, not which of its CELLS are closed. The month-vs-
     cutoff comparison is the real test, and it is the one §12b itself uses
     (`if (m <= cutoff) return;`, runFullModel.js:704).

   PURE. No I/O, no React, no DB. Two readers, one derivation: the operator-facing
   gate and the payload patch both call `reconcilePrepaidImport`, so what the screen
   names and what the save writes cannot disagree.
   ═══════════════════════════════════════════════════════════════════════════════ */

import { deriveAmortLag } from './prepaidAmort';

const r2 = n => Math.round(n * 100) / 100;

/* ★ THE ROW-MATCH KEY — the import's OWN upsert key, minus `event_identifier`.
 *
 * The live index (measured):
 *   prepaid_contracts_unique ON (company_id, vendor_name, start_month, length_months,
 *                                prepaid_amount, event_identifier) NULLS NOT DISTINCT
 * `event_identifier` is NULL on every import-written row and the index is NULLS NOT
 * DISTINCT, so these four columns ARE the effective per-company collision key — the
 * same four the parser's own `duplicate_key` notice uses. Matching on anything else
 * would let this module and the server disagree about which stored row a payload row
 * replaces, which is the whole failure it exists to prevent. */
export function prepaidRowKey(c) {
  return [
    String((c && c.vendor_name) || '').trim(),
    (c && c.start_month) || '',
    c && c.length_months == null ? 'NULL' : Number(c.length_months),
    Number((c && c.prepaid_amount) || 0),
  ].join('||');
}

const forecastKeys = (amort, cutoff) => Object.keys(amort || {}).filter(m => m > cutoff).sort();

/* ★ THE CONDITION — TWO ARMS, both forecast-only.
 *
 *   ARM 1 "partial" — a stored row HAS a payload twin, and the twin's `amort` LACKS a
 *                     month > cutoff that the stored row carries.
 *   ARM 2 "orphan"  — a stored row carrying at least one month > cutoff has NO payload
 *                     twin at all (its key changed, or the workbook dropped it).
 *
 * ★ WHY ARM 2 EXISTS AND IS NOT A DIFFERENT DEFECT. Measured on GovSpend: the SAME
 * operator hand-correction sits on cid=3 (start 2022-12, len 12) and on cid=8 (len 6).
 * On cid=3 the length still matches the workbook so the row MATCHES and arm 1 catches
 * it; on cid=8 the operator also changed the length, so the key no longer matches and
 * the row would vanish with arm 1 silent. It is one defect, detectable through two
 * paths, because the matching key contains a field the operator can edit.
 *
 * ⛔ AN ORPHAN WITH ONLY CLOSED CELLS IS OUT OF SCOPE, DELIBERATELY. The one such row
 * on every tenant is `Conferences 2022-01 $26,000.00` with a single 2022-05 cell —
 * and that row is the RTM $13,000 COLLISION (the workbook holds three rows at that
 * key: $13,000 / $13,000 / $14,250; $26,000 is the two 13k rows summed by the 4d
 * migration). That is §12.5 IMPORT-MUST-REPLACE mask #1, a conflict-key-identity
 * defect, filed and explicitly ruled OUT of this build. Re-adding it here would (a)
 * touch a closed cell, against the forecast-only fence, and (b) quietly take a
 * position on a data-identity question this build was told not to answer.
 *
 * @param payloadRows  the OUTGOING prepaid payload (already through the fill)
 * @param storedRows   the contracts currently in the DB, in onboarding shape
 * @param cutoff       companies.close_month
 * @returns { affected: [...], fingerprint: string }
 */
export function reconcilePrepaidImport(payloadRows, storedRows, cutoff) {
  const affected = [];
  /* ★ NO CUTOFF ⇒ SKIP, NEVER JUDGE. Without a close month there is no forecast/closed
   * boundary, so every cell would look forecast and the gate would fire on everything.
   * The same shape as P12/P13's skip guards: a check that cannot see its input must
   * say nothing, not guess. (The payload builder already skips its own fill on a null
   * cutoff — AdminPage `if (_cutoff)` — so this agrees with it by construction.) */
  if (!cutoff || !Array.isArray(payloadRows) || !Array.isArray(storedRows)) {
    return { affected, fingerprint: '' };
  }

  /* FIRST-WINS on a duplicate key, mirroring what the server's ON CONFLICT DO UPDATE
   * effectively leaves behind. A payload CAN carry two rows on one key (the RTM pair);
   * this module must not pretend otherwise. */
  const payByKey = new Map();
  for (const p of payloadRows) {
    const k = prepaidRowKey(p);
    if (!payByKey.has(k)) payByKey.set(k, p);
  }

  /* THE OPERATOR'S OWN ROW (2026-10-29). A contract read back from the database carries client_key 'id:<its stored id>'
   * (step6PrepaidFromGetRow), and an edit in the Step-6 editor keeps it (step6PrepaidFromModal spreads the row). When the
   * payload carries a row with that key, the stored contract IS on this screen -- possibly edited (an amount or term edit
   * changes prepaidRowKey, which used to make the stored row an "orphan": "the whole contract would be dropped", and its
   * pre-selected Keep then posted the old row beside the edited one). It is not a schedule this gate protects: the payload
   * row is the operator's answer for it. A PARSED row (a re-import from a workbook) carries no client_key, so the gate's
   * real job -- a workbook that would drop schedules entered in the app -- is unchanged. */
  const ownKeys = new Set();
  for (const p of payloadRows) if (p && typeof p.client_key === 'string' && p.client_key.startsWith('id:')) ownKeys.add(p.client_key);

  for (const st of storedRows) {
    if (st && typeof st.client_key === 'string' && ownKeys.has(st.client_key)) continue;
    const key = prepaidRowKey(st);
    const storedAmort = st.amort || {};
    const pay = payByKey.get(key);

    if (!pay) {
      const months = forecastKeys(storedAmort, cutoff);
      if (!months.length) continue;            // closed-only orphan → OUT OF SCOPE (above)
      affected.push({
        kind: 'orphan', key, months,
        vendor_name: st.vendor_name, account: st.account || null,
        start_month: st.start_month, length_months: st.length_months,
        prepaid_amount: Number(st.prepaid_amount) || 0,
        dollars: r2(months.reduce((t, m) => t + Number(storedAmort[m] || 0), 0)),
        storedAmort, stored: st,
      });
      continue;
    }

    const payAmort = pay.amort || {};
    const months = forecastKeys(storedAmort, cutoff).filter(m => !(m in payAmort));
    if (!months.length) continue;
    affected.push({
      kind: 'partial', key, months,
      vendor_name: st.vendor_name, account: st.account || null,
      start_month: st.start_month, length_months: st.length_months,
      prepaid_amount: Number(st.prepaid_amount) || 0,
      dollars: r2(months.reduce((t, m) => t + Number(storedAmort[m] || 0), 0)),
      storedAmort, stored: st,
    });
  }

  return { affected, fingerprint: prepaidReconcileFingerprint(affected) };
}

/* ★ THE FINGERPRINT IS WHAT THE OPERATOR ACKNOWLEDGES, NOT A BOOLEAN.
 * A sticky yes/no would nag on all four "Save Progress" buttons (a §12.5 shape-10
 * check nobody reads) OR, worse, would let a LATER upload put new rows at risk under
 * an acknowledgement given for an earlier one. Acknowledging the SET means a changed
 * set re-asks and an unchanged set stays quiet. */
export function prepaidReconcileFingerprint(affected) {
  return (affected || [])
    .map(a => `${a.kind}|${a.key}|${a.months.join(',')}`)
    .sort()
    .join(';');
}

/* ★ THE KEEP ARM — "keep the schedules I entered".
 *
 * FOR AN AFFECTED ROW, THE STORED FORECAST HALF WINS WHOLE; the workbook keeps the
 * closed half. NOT a union: merging the generated tail with the stored cells would
 * push Σ(amort) past `prepaid_amount` on any row where the two overlap, inventing
 * expense that neither side asked for. Replacing the forecast half is also what the
 * operator's answer MEANS — measured on Salesforce.com crm 2023-10, where the stored
 * schedule runs 2023-11..2024-10 (lag 1) and the generator produces 2023-10..2024-09
 * (lag 0, because the sibling lags disagree): a union would book thirteen months.
 * ⓘ CONSEQUENCE, STATED SO IT IS NOT A SURPRISE: on an affected row the generator's
 *   forecast is DISCARDED, not blended. That is the point — for these rows the
 *   generator is the thing that was overwriting the operator.
 *
 * UNAFFECTED ROWS ARE NOT TOUCHED AT ALL. Rows whose only difference is a rounding
 * remainder (cid=3 carries four, ≤$0.10) are not in `affected`, so they keep the
 * workbook's cents — this arm never becomes a schedule-wide preservation.
 *
 * ★ AND THE LAG IS RE-DERIVED FROM THE DICT BEING WRITTEN, through the ONE engine
 * (`deriveAmortLag`, prepaidAmort.js) — the 3a rule that the stored lag must describe
 * the stored schedule. Patching `amort` without re-deriving would leave a lag
 * describing a schedule that no longer exists, and PrepaidModal's Regenerate reads
 * that lag: `buildCycleAmort(addMonths(start, lag), …)`.
 *
 * Returns a NEW array; `payloadRows` is not mutated.
 */
export function applyPrepaidKeep(payloadRows, affected, cutoff, opts) {
  const rows = (payloadRows || []).map(r => ({ ...r }));
  if (!cutoff || !(affected || []).length) return rows;
  const options = opts || {};
  const resolveLine = typeof options.resolveIsLine === 'function' ? options.resolveIsLine : null;

  const idxByKey = new Map();
  rows.forEach((r, i) => { const k = prepaidRowKey(r); if (!idxByKey.has(k)) idxByKey.set(k, i); });

  const relag = (row, amort) => deriveAmortLag({
    vendor_name: row.vendor_name,
    // The event carve-out keys on vendor_name / conference_name / event_identifier.
    // Pass through whatever the row carries — the payload builder's own call
    // (AdminPage `amort_lag_months: deriveAmortLag({…})`) does exactly the same, and
    // every live row has event_identifier NULL, so a Conferences row still resolves to
    // lag 0 by vendor_name alone. ⛔ Do NOT synthesise a marker here.
    conference_name: row.conference_name,
    event_identifier: row.event_identifier != null ? row.event_identifier : null,
    start_month: row.start_month,
    length_months: row.length_months,
    prepaid_amount: row.prepaid_amount,
    amort,
  });

  for (const a of affected) {
    const storedForecast = {};
    for (const m of forecastKeys(a.storedAmort, cutoff)) storedForecast[m] = a.storedAmort[m];

    if (a.kind === 'partial') {
      const i = idxByKey.get(a.key);
      if (i == null) continue;                    // defensive: the twin that defined the arm
      const cur = rows[i];
      const closed = {};
      for (const m of Object.keys(cur.amort || {})) if (m <= cutoff) closed[m] = cur.amort[m];
      const next = { ...closed, ...storedForecast };
      rows[i] = { ...cur, amort: next, amort_lag_months: relag(cur, next) };
      continue;
    }

    // ARM 2 — re-add the orphan, carrying its WHOLE stored schedule (it has no twin,
    // so there is no workbook history to prefer). sort_order appends after the parsed
    // set: it is display order (Prepaid Sch position) and the row is not in that sheet.
    const st = a.stored || {};
    const amort = st.amort || {};
    const row = {
      vendor_name: st.vendor_name,
      account: st.account || null,
      /* The server matches a duplicate vendor NAME on it and (since 2026-09-02) stores it.
       * The caller's resolver decides it -- since 2026-10-29 the GET re-map carries the
       * stored `is_line`, and AdminPage's resolver goes slug-first on it, then the label.
       * (This note previously read: "TRANSIENT ... never stores it. Resolved from the
       * ACCOUNT LABEL only ... the GET re-map deliberately does not carry `is_line`".) */
      resolved_is_line: resolveLine ? (resolveLine(st) || null) : null,
      start_month: st.start_month,
      length_months: st.length_months == null ? null : Math.round(Number(st.length_months)),
      prepaid_amount: Number(st.prepaid_amount) || 0,
      amort,
      /* ★ THE EVENT-BOOKING MARKER TRAVELS WITH THE RESCUED ROW (the §6 follow-up).
       *
       * WITHOUT THIS LINE, KEEP was self-contradicting on exactly one shape: it would
       * rescue an event-style orphan's SCHEDULE and drop the marker that says what the
       * schedule IS. The import re-creates the row from this payload, so an omitted field
       * is a NULL column — the same payload-boundary omission class item 4 exists to
       * close, reappearing one branch over.
       * ⓘ NOTHING ELSE CHANGES: the server already reads `p.event_month ?? null` off EVERY
       *   payload element (server.js:3285, item 4), so the row simply has to CARRY it.
       *   There is no second plumbing change — this is not a new path, it is a field that
       *   was being dropped on an existing one.
       * ⓘ AND IT IS LAG-INERT BY CONSTRUCTION: `deriveAmortLag` decides the event class
       *   through `isPrepaidEventStyle`, which reads vendor_name / conference_name /
       *   event_identifier and NEVER event_month. So `relag` below cannot see this field
       *   and no schedule moves because of it.
       * DUAL-READ, matching `step6PrepaidFromGetRow`: AdminPage hands this function rows
       * already normalized to snake_case, but a caller passing RAW `getPrepaidContracts`
       * output would carry the GET's camelCase `eventMonth` (server.js:985). Reading both
       * means the two callers cannot disagree about whether the marker was present.
       * ⛔ `is_line` and `event_identifier` are STILL NOT forwarded — §12.5 P-2 and the
       *   prepaid_contracts_unique conflict key, untouched here exactly as everywhere else. */
      event_month: st.eventMonth ?? st.event_month ?? null,
      is_historical: st.is_historical !== false,
      sort_order: rows.length,
      /* Prepaid renewals part 1 (2026-10-29): a rescued row keeps its Renews answer and its renewal keys (AdminPage's
       * step6PrepaidFromGetRow sets them on the stored rows; dual-read for a raw GET row), the same payload-boundary rule as
       * event_month above. */
      renews: st.renews === true ? true : st.renews === false ? false : null,
      client_key: st.client_key ?? (st.id != null ? 'id:' + st.id : null),
      renewal_of_key: st.renewal_of_key ?? (st.renewalOfId != null ? 'id:' + st.renewalOfId : null),
    };
    row.amort_lag_months = relag(row, amort);
    rows.push(row);
    idxByKey.set(a.key, rows.length - 1);
  }

  return rows;
}
